import { INVENTORY_MODULE } from "@/api_framework/api_config";
import { useAPIUtils } from "@/app/hooks/useAPIUtils";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import axios, { AxiosError } from "axios";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";

export type ClinicToDoctorRequestAction = "accept" | "reject" | "cancel";

interface IRequestActionBody {
  clinic_id: string;
  reason?: string;
}

interface IRequestActionResponse {
  status: boolean;
  message: string;
}

const ACTION_MESSAGES: Record<ClinicToDoctorRequestAction, { success: string; error: string }> = {
  accept: { success: "Request accepted successfully", error: "Failed to accept request" },
  reject: { success: "Request rejected successfully", error: "Failed to reject request" },
  cancel: { success: "Transfer request cancelled successfully", error: "Failed to cancel transfer request" },
};

export const useClinicToDoctorRequestActionAPI = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const configuration = useConfiguration();
  const { captureError } = useAPIUtils();

  const execute = useCallback(
    async (
      requestId: string,
      action: ClinicToDoctorRequestAction,
      body: IRequestActionBody,
    ): Promise<{ status: boolean }> => {
      const messages = ACTION_MESSAGES[action];
      setIsSubmitting(true);
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const res = await axios.post<IRequestActionResponse>(
          `${INVENTORY_MODULE.CLINIC_TO_DOCTOR_REQUEST.baseURL}/${requestId}/${action}`,
          body,
          {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        );
        if (res.data?.status === true) {
          toast.success(res.data.message || messages.success);
          return { status: true };
        } else {
          toast.error(res.data?.message || messages.error);
          return { status: false };
        }
      } catch (e: any) {
        if (e instanceof AxiosError) {
          captureError(e);
        }
        toast.error(e?.response?.data?.message || messages.error);
        return { status: false };
      } finally {
        setIsSubmitting(false);
      }
    },
    [],
  );

  return { execute, isSubmitting };
};
